import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import store from "../redux/store";
import styles from "../styles/shared/GuideChat.module.css";
import { extraReads, pageKind, publicAnswer, clampPanel, coinsInQuestion } from "./guideFacts";
import { buildGuidePayload, rowsFromSuggestions } from "./signalBrief";
import { normalizeLiveTimeframe, setLiveAiTimeframe } from "../redux/live-suggestion/liveSuggestionSlice";
import { DeepCards, runDeepCheck } from "../components/trading-assistant/assistant/OnDemandReport";
import { marketQuote } from "./marketQuote";
import { stockDaily, stockTarget } from "./stockDaily";
import { HiOutlineGlobeAlt } from "react-icons/hi";
import { BsCalendar2Week } from "react-icons/bs";
import { BiCategory } from "react-icons/bi";
import {
  MdTrendingUp,
  MdTimelapse,
  MdCompareArrows,
  MdInsights,
  MdHowToVote,
  MdTimer,
  MdContentCopy,
  MdThumbUp,
  MdThumbDown,
  MdPieChart,
  MdBlock,
  MdShowChart,
  MdFlag,
  MdPictureAsPdf,
} from "react-icons/md";

export const OPEN_GUIDE_CHAT = "cc-open-guide-chat";

export function openGuideChat(question) {
  const detail =
    typeof question === "string"
      ? question
      : question?.deep === "markets" || question?.deep === "weekly"
        ? { deep: question.deep, symbol: question.symbol || "" }
        : "";
  window.dispatchEvent(new CustomEvent(OPEN_GUIDE_CHAT, { detail }));
}

const GUIDE_API =
  "https://hav8w1xk8h.execute-api.ap-southeast-2.amazonaws.com/dev/api";
const SIZE_KEY = "cc-guide-size-lg";
const CHAT_KEY = "cc-guide-log";
const WELCOME = {
  role: "guide",
  text: "The assistant row is the category call, such as Technical Indicators or On-chain. Pivot looks for a turn on the timeframe you pick. For a longer hold, follow the category call. If they disagree, the pivot is not a coin to add.",
  sources: [],
};

function readChat() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CHAT_KEY) || "");
    if (Array.isArray(saved) && saved.length) return saved.slice(-30);
  } catch (error) {
    return [WELCOME];
  }
  return [WELCOME];
}

function namedCoin(text) {
  const raw = String(text || "").replace(/\b(xau|xag)\s+usd\b/ig, (_, metal) => `${metal.toUpperCase()}USD`);
  const asking = /\b(analys(?:e|is)|check|across|markets)\b/i.test(raw)
    || /\bis\b[\s\S]{0,40}\b(good|worth)\b/i.test(raw)
    || /\bshould i (buy|trade|hold)\b/i.test(raw);
  if (!asking) return "";
  const metal = raw.toUpperCase().match(/\b(XAU|XAG)USD\b/);
  if (metal) return metal[0];
  const known = coinsInQuestion(raw)[0];
  if (known) return known;
  const match = raw.match(/\b(?:analys(?:e|is)|check)\s+([A-Za-z0-9]{2,10})\b/i);
  return match ? coinsInQuestion(match[1].toUpperCase())[0] || "" : "";
}

function stamp(message) {
  return { at: new Date().toISOString(), sources: [], ...message };
}

function whenText(at) {
  const date = new Date(at || "");
  if (!at || Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function pdfLines(text, cards) {
  const lines = [text];
  (cards || []).forEach((card) => {
    lines.push([card.symbol, card.action, card.side].filter(Boolean).join(" "));
    if (card.hour) lines.push(`1h pivot ${card.hour}`);
    if (card.daily) lines.push(`Daily pivot ${card.daily}`);
    (card.others || []).filter(Boolean).forEach((line) => lines.push(line));
    if (card.line) lines.push(card.line);
  });
  return lines.filter(Boolean);
}

function pdfBlob(lines) {
  const safe = lines.map((line) => String(line).replace(/[^\x20-\x7E]/g, " ").slice(0, 90)).slice(0, 42);
  const body = ["BT", "/F1 11 Tf", "48 760 Td", "14 TL"];
  safe.forEach((line, index) => {
    const text = line.replace(/\\/g, "\\\\").replace(/\(/g, "\\(").replace(/\)/g, "\\)");
    body.push(index ? `T* (${text}) Tj` : `(${text}) Tj`);
  });
  body.push("ET");
  const stream = body.join("\n");
  const objects = [
    "1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj",
    "2 0 obj << /Type /Pages /Count 1 /Kids [3 0 R] >> endobj",
    "3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >> endobj",
    `4 0 obj << /Length ${stream.length} >> stream\n${stream}\nendstream endobj`,
    "5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj",
  ];
  let file = "%PDF-1.4\n";
  const offsets = [];
  objects.forEach((obj) => {
    offsets.push(file.length);
    file += `${obj}\n`;
  });
  const xref = file.length;
  file += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => {
    file += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  file += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([file], { type: "application/pdf" });
}

function downloadPdf(lines) {
  const url = URL.createObjectURL(pdfBlob(lines));
  const link = document.createElement("a");
  link.href = url;
  link.download = "guide-report.pdf";
  link.click();
  URL.revokeObjectURL(url);
}
const DEFAULT_WIDTH = 560;
const DEFAULT_HEIGHT = 720;

function readAnswer(payload) {
  let data = payload || {};
  if (typeof data.body === "string") {
    try {
      data = JSON.parse(data.body);
    } catch (error) {
      data = payload || {};
    }
  }
  return data;
}

function savedSize() {
  try {
    const saved = JSON.parse(sessionStorage.getItem(SIZE_KEY) || "");
    if (saved?.width && saved?.height) {
      return clampPanel(saved.width, saved.height, window);
    }
  } catch (error) {
    return clampPanel(DEFAULT_WIDTH, DEFAULT_HEIGHT, window);
  }
  return clampPanel(DEFAULT_WIDTH, DEFAULT_HEIGHT, window);
}

function clampSize(width, height) {
  return clampPanel(width, height, window);
}

function dumpedAnswer(text) {
  return (
    text.includes("Here is what is on your board") ||
    text.includes("Weigh the AI Assistant board first") ||
    /\b(LONG|TERM|MID|SHORT) is not on the board/.test(text)
  );
}

function wantsBook(question) {
  return /portfolio|percent|percentage|what should i (buy|hold)|what to buy|for \d|months?|weeks?|disagree/i.test(
    String(question || ""),
  );
}

function bookRows(coins) {
  const rows = Array.isArray(coins) ? coins : [];
  const seen = new Set();
  const picked = [];
  rows.forEach((row) => {
    if (!row?.symbol || seen.has(row.symbol)) return;
    if (row.side !== "Buy" && row.side !== "Sell") return;
    seen.add(row.symbol);
    picked.push(row);
  });
  return picked;
}

function clashRows(coins) {
  const rows = Array.isArray(coins) ? coins : [];
  const seen = new Set();
  const picked = [];
  rows.forEach((row) => {
    if (!row?.symbol || seen.has(row.symbol)) return;
    const live = row.live;
    if ((live !== "Buy" && live !== "Sell") || live === row.side) return;
    seen.add(row.symbol);
    picked.push(row);
  });
  return picked;
}

function sizeText(row) {
  const size = Number(row?.size);
  if (!Number.isFinite(size)) return "";
  return `${Number.isInteger(size) ? size : size.toFixed(1)}%`;
}

function dropInventory(text, coins) {
  const symbols = new Set((coins || []).map((coin) => String(coin.symbol || "").toUpperCase()));
  const kept = String(text || "")
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => {
      const named = (sentence.match(/\b[A-Z]{2,6}\b/g) || []).filter((word) => symbols.has(word));
      if (named.length >= 2) return false;
      return !/quiet pivot|stay off the book|listed coins|remain on Hold|sit at a Hold|off the book/i.test(
        sentence,
      );
    });
  const short = kept.join(" ").replace(/\s{2,}/g, " ").trim();
  if (short) return short;
  const first = String(text || "").split(/(?<=[.!?])\s+/)[0] || "";
  return first.trim();
}

function visibleAnswer(data, board, question) {
  let text = String(data?.answer || "").trim();
  if (data?.limited) return text;
  if (!text || dumpedAnswer(text)) text = publicAnswer(board);
  text = text.replace(/\b[A-Z0-9]{2,12} is not on the (?:open )?(?:Insights pack|board)[^.]*\./g, (sentence) => {
    const word = sentence.split(" ")[0];
    return coinsInQuestion(word).length ? sentence : "";
  });
  const buys = (board?.coins || []).filter((coin) => coin.side === "Buy").map((coin) => coin.symbol);
  if (buys.length && /no buy book/i.test(text)) {
    text = text.replace(
      /there is no buy book to allocate[^.]*\./gi,
      `The Buy on this board is ${buys.join(", ")}. The Hold coins are not a book to add.`,
    );
  }
  text = text.replace(/\s{2,}/g, " ").trim();
  if (wantsBook(question)) text = dropInventory(text, board?.coins);
  return text;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const LIVE_FRAMES = ["15m", "1h", "4h", "1d", "1w"];

async function askPlan(question, previous) {
  const response = await axios.post(
    GUIDE_API,
    {
      platform_mode: "guide_chat",
      question,
      previous_question: previous,
      page: pageKind(window.location.pathname),
      plan_only: "True",
    },
    { timeout: 8000 },
  );
  return readAnswer(response?.data);
}

async function followPlan(plan) {
  const requested = String(plan?.live_timeframe || "");
  if (!LIVE_FRAMES.includes(requested)) return;
  const state = store.getState();
  if ((state?.tradingAssistant?.aiMarketType || "crypto") !== "crypto") return;
  if (normalizeLiveTimeframe(state?.liveSuggestion?.liveTimeframe) !== requested) {
    store.dispatch(setLiveAiTimeframe(requested));
  }
  const started = Date.now();
  while (Date.now() - started < 18000) {
    const next = store.getState();
    const coins = (next?.tradingAssistant?.tradeAssistant?.ASSISTANT_SUGGESTIONS || [])
      .map((item) => String(item?.SYMBOL_NAME || "").split("/")[0].toUpperCase())
      .filter(Boolean);
    const loaded = new Set(
      (next?.liveSuggestion?.suggestions || [])
        .filter((item) => normalizeLiveTimeframe(item?.timeframe) === requested)
        .map((item) => String(item?.symbol || "").split("/")[0].toUpperCase()),
    );
    if (coins.length && coins.every((coin) => loaded.has(coin))) return;
    await sleep(500);
  }
}

async function askGuide(question, signals, board, previous) {
  const userData = store.getState()?.main?.userData || {};
  const response = await axios.post(GUIDE_API, {
    platform_mode: "guide_chat",
    question,
    previous_question: previous,
    page: pageKind(window.location.pathname),
    signals,
    board,
    user: userData["custom:custom_username"] || "",
    email: userData["cognito:username"] || "",
  });
  return readAnswer(response?.data);
}

async function readCachedBoard(email, user, market, mode) {
  const payload = {
    platform_mode: "trade_assistant",
    email,
    user,
    request_type: "get_trade_suggestion",
    portfolio_type: "primary",
    market_type: market,
  };
  if (mode && mode !== "default" && mode !== "null") {
    payload.ai_assistant_mode = mode;
  }
  const response = await axios.post(GUIDE_API, payload);
  const data = readAnswer(response?.data);
  if (data?.status === "miss" || !Array.isArray(data?.ASSISTANT_SUGGESTIONS)) return null;
  return data;
}

async function loadComparisons(state, question) {
  if (pageKind(window.location.pathname) === "insight") return [];
  const userData = state?.main?.userData || {};
  const email = userData["cognito:username"];
  const user = userData["custom:custom_username"];
  if (!user || !email) return [];
  const reads = extraReads(
    question,
    state?.tradingAssistant?.aiMarketType || "crypto",
    state?.tradingAssistant?.aiAssistantMode,
  );
  const loaded = await Promise.all(
    reads.map(async (read) => {
      try {
        const board = await readCachedBoard(email, user, read.market, read.mode);
        if (!board) return { label: read.label, missing: true };
        const rows = rowsFromSuggestions(board.ASSISTANT_SUGGESTIONS);
        const buy = rows.filter((row) => row.side === "Buy").length;
        const sell = rows.filter((row) => row.side === "Sell").length;
        return {
          label: read.label,
          mix: { buy, sell, hold: rows.length - buy - sell },
          coins: rows.slice(0, 4).map((row) => ({
            symbol: row.coin,
            side: row.side,
            strength: row.strength,
          })),
        };
      } catch (error) {
        return { label: read.label, missing: true };
      }
    }),
  );
  return loaded;
}

function MixBar({ mix }) {
  const buy = Number(mix?.buy) || 0;
  const sell = Number(mix?.sell) || 0;
  const hold = Number(mix?.hold) || 0;
  const total = buy + sell + hold;
  if (!total) return null;
  return (
    <div className={styles.mix}>
      <div className={styles.bar} aria-hidden="true">
        <span className={styles.buyBar} style={{ width: `${(buy / total) * 100}%` }} />
        <span className={styles.holdBar} style={{ width: `${(hold / total) * 100}%` }} />
        <span className={styles.sellBar} style={{ width: `${(sell / total) * 100}%` }} />
      </div>
      <p className={styles.mixText}>
        {buy} Buy · {hold} Hold · {sell} Sell
      </p>
    </div>
  );
}

function sideTone(side) {
  return styles[String(side || "Hold").toLowerCase()] || styles.hold;
}

function PortfolioTable({ coins }) {
  const book = bookRows(coins);
  const clashes = clashRows(coins);
  if (!book.length && !clashes.length) {
    return <p className={styles.mixText}>Nothing to add. Hold is not a coin to open.</p>;
  }
  return (
    <>
      {book.length ? (
        <table className={styles.book}>
          <caption>Suggested book</caption>
          <thead>
            <tr>
              <th>Coin</th>
              <th>Assistant</th>
              <th>Pivot</th>
              <th>Size</th>
            </tr>
          </thead>
          <tbody>
            {book.map((coin) => (
              <tr key={coin.symbol}>
                <td>{coin.symbol}</td>
                <td className={sideTone(coin.side)}>{coin.side}</td>
                <td className={sideTone(coin.live)}>{coin.live || "—"}</td>
                <td>{sizeText(coin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className={styles.mixText}>Nothing to add. Hold is not a coin to open.</p>
      )}
      {clashes.length > 0 && (
        <div className={styles.clash}>
          <p className={styles.mixText}>Pivot disagrees. Follow the category call.</p>
          <ul>
            {clashes.map((coin) => {
              const call = coin.side || "Hold";
              const inBook = call === "Buy" || call === "Sell";
              return (
                <li key={coin.symbol}>
                  {coin.symbol} {inBook ? "is in the book." : "stays off the book."} The pivot says{" "}
                  <span className={sideTone(coin.live)}>{coin.live}</span>. The category call says{" "}
                  <span className={sideTone(call)}>{call}</span>.
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </>
  );
}

export const DEEP_LINKS = [
  { id: "markets", label: "All markets", hint: "Best calls on crypto, forex, and stock", icon: HiOutlineGlobeAlt },
  { id: "weekly", label: "Weekly picks", hint: "Refresh the weekly list, then check the top coins", icon: BsCalendar2Week },
];

export const INSIGHT_SCENARIOS = [
  { label: "Category leaders", ask: "Which category is leading this pack?", icon: BiCategory },
  { label: "Category clash", ask: "Where do the categories disagree on the same coin?", icon: MdCompareArrows },
  { label: "Sentiment and macro", ask: "What do sentiment and the macro categories say?", icon: MdInsights },
  { label: "Same direction", ask: "Which coins have categories pointing the same way?", icon: MdHowToVote },
];

export const PRICE_EXAMPLES = [
  { label: "price AAPL", ask: "price AAPL", icon: MdShowChart },
  { label: "price BHP.AX", ask: "price BHP.AX", icon: MdShowChart },
  { label: "price EURUSD", ask: "price EURUSD", icon: MdShowChart },
  { label: "فملی", ask: "فملی", icon: MdShowChart },
  { label: "شپنا", ask: "شپنا", icon: MdShowChart },
  { label: "خودرو", ask: "خودرو", icon: MdShowChart },
  { label: "daily AAPL", ask: "daily AAPL", icon: MdShowChart },
  { label: "daily BBCA.JK", ask: "daily BBCA.JK", icon: MdShowChart },
  { label: "daily فملی", ask: "daily فملی", icon: MdShowChart },
];

export const BOARD_QUESTIONS = [
  { label: "Buy for 3 months", ask: "What should I buy for the next 3 months?", icon: MdTrendingUp },
  { label: "Hold for 2 weeks", ask: "For 2 weeks, what should I hold and what percentage?", icon: MdTimelapse },
  { label: "Pivot disagrees", ask: "Which coins disagree with the pivot?", icon: MdCompareArrows },
  { label: "Which category", ask: "What category is this assistant call from?", icon: BiCategory },
  { label: "What size", ask: "What percentage should I use on this trade?", icon: MdPieChart },
  { label: "Skip the sells", ask: "What should I skip this week?", icon: MdBlock },
  { label: "Forex board", ask: "What about forex?", icon: MdShowChart },
  { label: "Stop and target", ask: "Where is the stop and the target?", icon: MdFlag },
];

export const INSIGHT_QUESTIONS = [
  { label: "What stands out", ask: "What stands out on this Insights pack?", icon: MdInsights },
  { label: "Pivot disagrees", ask: "Which coins disagree with the pivot?", icon: MdCompareArrows },
  { label: "Strongest votes", ask: "Which indicator votes are the strongest?", icon: MdHowToVote },
  { label: "Act or wait", ask: "Should I act on this pack or wait?", icon: MdTimer },
  { label: "What size", ask: "What percentage should I use?", icon: MdPieChart },
  { label: "Leave alone", ask: "Which coins should I leave alone this week?", icon: MdBlock },
];

function GuideChat() {
  const [open, setOpen] = useState(false);
  const [size, setSize] = useState(savedSize);
  const sizeRef = useRef(size);
  const logRef = useRef(null);
  const lastQuestionRef = useRef("");
  const sendRef = useRef(() => {});

  useEffect(() => {
    const openChat = (event) => {
      setOpen(true);
      const detail = event?.detail;
      if (detail && typeof detail === "object") {
        sendRef.current(detail);
        return;
      }
      const text = String(detail || "").trim();
      if (text) sendRef.current(text);
    };
    window.addEventListener(OPEN_GUIDE_CHAT, openChat);
    return () => window.removeEventListener(OPEN_GUIDE_CHAT, openChat);
  }, []);

  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState("Checking the board…");
  const [messages, setMessages] = useState(readChat);

  useEffect(() => {
    try {
      sessionStorage.setItem(CHAT_KEY, JSON.stringify(messages.slice(-30)));
    } catch (error) {
      return;
    }
  }, [messages]);

  useEffect(() => {
    const node = logRef.current;
    if (!open || !node) return;
    node.scrollTop = node.scrollHeight;
  }, [open, messages, busy, thinking]);

  const rememberSize = (next) => {
    const clamped = clampSize(next.width, next.height);
    sizeRef.current = clamped;
    setSize(clamped);
    sessionStorage.setItem(SIZE_KEY, JSON.stringify(clamped));
  };

  const startResize = (event) => {
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const start = sizeRef.current;
    const move = (ev) => {
      rememberSize({
        width: start.width + (startX - ev.clientX),
        height: start.height + (startY - ev.clientY),
      });
    };
    const stop = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);
  };

  const sendText = async (raw) => {
    const link = raw && typeof raw === "object" ? DEEP_LINKS.find((item) => item.id === raw.deep) : null;
    const named = raw && typeof raw === "object" ? String(raw.symbol || "").split("/")[0].trim() : "";
    const text = named ? `Analyse ${named}` : link ? link.label : String(raw || "").trim();
    const coin = named || (link ? "" : namedCoin(text));
    if (!text || busy || (raw && typeof raw === "object" && !link)) return;
    setQuestion("");
    setMessages((current) => [...current, stamp({ role: "user", text })]);
    if (!link && !coin && /^(analys(?:e|is)|check)$/i.test(text)) {
      setMessages((current) => [
        ...current,
        stamp({ role: "guide", text: "Name a coin, such as analyse BTC." }),
      ]);
      return;
    }
    const stock = !link ? stockTarget(text) : "";
    if (stock) {
      setBusy(true);
      setThinking("Reading the daily call…");
      try {
        const line = await stockDaily(stock);
        setMessages((current) => [...current, stamp({ role: "guide", text: line })]);
      } catch (error) {
        setMessages((current) => [...current, stamp({ role: "guide", text: "That daily call did not load." })]);
      } finally {
        setBusy(false);
      }
      return;
    }
    const quote = !link && !coin ? marketQuote(text) : null;
    if (quote) {
      setBusy(true);
      setThinking("Reading the price…");
      try {
        const line = await quote;
        setMessages((current) => [...current, stamp({ role: "guide", text: line })]);
      } catch (error) {
        setMessages((current) => [...current, stamp({ role: "guide", text: "That price did not load." })]);
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    if (link || coin) {
      setThinking(link?.id === "weekly" ? "Updating the weekly list…" : "Checking markets…");
      try {
        const result = await runDeepCheck(store.dispatch, link?.id || "markets", setThinking, coin);
        setMessages((current) => [
          ...current,
          stamp({
            role: "guide",
            text: result.text,
            deepCards: result.cards,
            pdf: pdfLines(result.text, result.cards),
          }),
        ]);
      } catch (error) {
        setMessages((current) => [
          ...current,
          stamp({ role: "guide", text: "This check did not finish. Try again." }),
        ]);
      } finally {
        setBusy(false);
      }
      return;
    }
    setThinking("Checking the board…");
    try {
      const previous = lastQuestionRef.current;
      const planned = await askPlan(text, previous).catch(() => ({}));
      if (planned?.source === "plan") await followPlan(planned.plan);
      lastQuestionRef.current = text;
      const state = store.getState();
      const comparisons = await loadComparisons(state, text);
      const payload = buildGuidePayload(state, text, comparisons);
      const data = await askGuide(text, payload.signals, payload.board, previous);
      const mix = data.mix || payload.board.mix;
      const coins = payload.board.coins?.length ? payload.board.coins : data.coins;
      const total = (Number(mix?.buy) || 0) + (Number(mix?.sell) || 0) + (Number(mix?.hold) || 0);
      setMessages((current) => [
        ...current,
        stamp({
          role: "guide",
          text: visibleAnswer(data, payload.board, text),
          sources: Array.isArray(data.sources) ? data.sources : [],
          mix,
          coins,
          showBook: wantsBook(text) && (total > 0 || Boolean(coins?.length)),
        }),
      ]);
    } catch (error) {
      const server = readAnswer(error?.response?.data);
      setMessages((current) => [
        ...current,
        stamp({
          role: "guide",
          text:
            server.answer ||
            server.MESSAGE ||
            "The guide chat is unavailable. Read the articles on cryptocrispy.com.au/news.html.",
          sources: [
            {
              title: "News and guides",
              url: "https://cryptocrispy.com.au/news.html",
            },
          ],
        }),
      ]);
    } finally {
      setBusy(false);
    }
  };
  const setVote = (index, vote) => {
    setMessages((current) => current.map((item, itemIndex) => {
      if (itemIndex !== index || item.role !== "guide") return item;
      return { ...item, vote: item.vote === vote ? "" : vote };
    }));
  };
  const copyMessage = (text) => {
    const value = String(text || "");
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(value).catch(() => {});
  };
  sendRef.current = sendText;

  return createPortal(
    <>
      <button
        type="button"
        className={styles.launcher}
        onClick={() => setOpen((value) => !value)}
      >
        Ask AI
      </button>
      {open && (
        <section
          className={styles.panel}
          style={{ width: size.width, height: size.height }}
          aria-label="Ask AI"
        >
          <button
            type="button"
            className={styles.resize}
            aria-label="Resize Ask AI"
            onMouseDown={startResize}
          />
          <div className={styles.header}>
            <strong>Ask AI</strong>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close">
              ×
            </button>
          </div>
          <div className={styles.log} ref={logRef} aria-busy={busy}>
            {messages.map((message, index) => (
              <div
                key={`${message.role}-${index}`}
                className={message.role === "user" ? styles.mine : styles.theirs}
              >
                <p className={message.role === "user" ? styles.user : styles.guide}>
                  {message.text}
                </p>
                {message.showBook && (
                  <>
                    <MixBar mix={message.mix} />
                    <PortfolioTable coins={message.coins} />
                  </>
                )}
                {message.deepCards?.length ? <DeepCards cards={message.deepCards} /> : null}
                {message.role === "guide" && (
                  <div className={styles.actions}>
                    <button type="button" aria-label="Copy" title="Copy" onClick={() => copyMessage(message.text)}>
                      <MdContentCopy />
                    </button>
                    <button
                      type="button"
                      aria-label="Like"
                      title="Like"
                      className={message.vote === "up" ? styles.voted : ""}
                      onClick={() => setVote(index, "up")}
                    >
                      <MdThumbUp />
                    </button>
                    <button
                      type="button"
                      aria-label="Dislike"
                      title="Dislike"
                      className={message.vote === "down" ? styles.voted : ""}
                      onClick={() => setVote(index, "down")}
                    >
                      <MdThumbDown />
                    </button>
                    {message.pdf?.length ? (
                      <button type="button" aria-label="PDF" title="PDF" onClick={() => downloadPdf(message.pdf)}>
                        <MdPictureAsPdf />
                      </button>
                    ) : null}
                  </div>
                )}
                {whenText(message.at) ? <p className={styles.when}>{whenText(message.at)}</p> : null}
                {message.sources?.length > 0 && (
                  <div className={styles.sources}>
                    {message.sources.map((source) => (
                      <a
                        key={source.url}
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {source.title}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {busy && (
              <p className={styles.thinking} role="status">
                {thinking}
              </p>
            )}
          </div>
          <div className={styles.starters}>
            {pageKind(window.location.pathname) === "insight"
              ? INSIGHT_SCENARIOS.map((item) => (
                  <button
                    key={item.ask}
                    type="button"
                    className={styles.curious}
                    disabled={busy}
                    title={item.ask}
                    onClick={() => sendText(item.ask)}
                  >
                    {item.icon ? <item.icon /> : null}
                    {item.label}
                  </button>
                ))
              : DEEP_LINKS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={item.id === "markets" ? styles.markets : styles.weekly}
                  disabled={busy}
                  title={item.hint}
                  onClick={() => sendText({ deep: item.id })}
                >
                  {item.icon ? <item.icon /> : null}
                  {item.label}
                </button>
            ))}
            {(pageKind(window.location.pathname) === "insight"
              ? INSIGHT_QUESTIONS
              : BOARD_QUESTIONS
            ).map((starter) => (
              <button
                key={starter.ask}
                type="button"
                disabled={busy}
                className={styles.curious}
                title={starter.ask}
                onClick={() => sendText(starter.ask)}
              >
                {starter.icon ? <starter.icon /> : null}
                {starter.label}
              </button>
            ))}
            <p className={styles.exampleLabel}>Price and daily examples</p>
            {PRICE_EXAMPLES.map((item) => (
              <button
                key={item.ask}
                type="button"
                disabled={busy}
                title={item.ask}
                onClick={() => sendText(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
          </div>
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault();
              sendText(question);
            }}
          >
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              placeholder="Why is BTC a sell?"
              maxLength={400}
              aria-label="Question"
            />
            <button type="submit" disabled={busy}>
              {busy ? "..." : "Ask"}
            </button>
          </form>
        </section>
      )}
    </>,
    document.body,
  );
}

export default GuideChat;
